import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import PageLayout from '../../components/ui/PageLayout';
import ScoringDashboard from './components/ScoringDashboard';
import ScoringConfiguration from './components/ScoringConfiguration';
import PrioritizationQueue from './components/PrioritizationQueue';
import ScoringBreakdown from './components/ScoringBreakdown';
import { leadScoringService } from '../../services/leadScoringService';
import { useAuth } from '../../contexts/AuthContext';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';

export default function AILeadScoringSystem() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [priorityLeads, setPriorityLeads] = useState([]);
  const [scoringFactors, setScoringFactors] = useState([]);
  const [scoringStats, setScoringStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.id) {
      loadData();
    } else {
      setLoading(false);
    }
  }, [user?.id]);

  const loadData = async () => {
    try {
      setLoading(true);
      setError('');

      const [leads, factors, stats] = await Promise.allSettled([
        leadScoringService?.getPriorityQueue(user?.id),
        leadScoringService?.getScoringFactors(),
        leadScoringService?.getLeadScoringStats(user?.id)
      ]);

      if (leads?.status === 'fulfilled') setPriorityLeads(leads?.value || []);
      if (factors?.status === 'fulfilled') setScoringFactors(factors?.value || []);
      if (stats?.status === 'fulfilled') setScoringStats(stats?.value?.data || null);
    } catch (err) {
      console.error('Error loading AI lead scoring data:', err);
      setError(err?.message || 'Failed to load lead scoring data');
    } finally {
      setLoading(false);
    }
  };

  const handleScoreUpdate = async (leadId, newScore) => {
    try {
      await leadScoringService?.updateLeadScore(leadId, newScore);
      await loadData();
    } catch (err) {
      console.error('Error updating lead score:', err);
    }
  };

  const handleLeadContacted = async (leadId) => {
    try {
      await leadScoringService?.markLeadContacted(leadId);
      await loadData();
    } catch (err) {
      console.error('Error marking lead as contacted:', err);
    }
  };

  const tabs = [
    { id: 'dashboard', label: 'Scoring Dashboard' },
    { id: 'prioritization', label: 'Lead Prioritization' },
    { id: 'configuration', label: 'Scoring Configuration' },
    { id: 'breakdown', label: 'Scoring Breakdown' }
  ];

  if (loading) {
    return (
      <PageLayout>
        <Helmet><title>AI Lead Scoring System - LeadGen Pro AI</title></Helmet>
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading AI lead scoring data...</p>
          </div>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout>
      <Helmet>
        <title>AI Lead Scoring System - LeadGen Pro AI</title>
        <meta name="description" content="Intelligent lead qualification and prioritization through advanced machine learning analysis" />
      </Helmet>

      <div className="flex flex-col lg:flex-row lg:items-center justify-between mb-8">
        <div>
          <h1 className="text-3xl font-bold text-foreground mb-2">AI Lead Scoring System</h1>
          <p className="text-muted-foreground">Intelligent lead qualification and prioritization</p>
        </div>
        <Button variant="outline" iconName="RefreshCw" iconPosition="left" onClick={loadData}>Refresh</Button>
      </div>

      {error && (
        <div className="bg-destructive/10 border border-destructive/20 rounded-lg p-4 mb-6 flex items-center space-x-3">
          <Icon name="AlertCircle" size={18} className="text-destructive" />
          <p className="text-sm text-destructive">{error}</p>
          <Button variant="ghost" size="sm" onClick={loadData}>Retry</Button>
        </div>
      )}

      {/* Tabs */}
      <div className="border-b border-border mb-6">
        <nav className="-mb-px flex space-x-6 overflow-x-auto">
          {tabs?.map(tab => (
            <button
              key={tab?.id}
              onClick={() => setActiveTab(tab?.id)}
              className={`py-3 px-1 border-b-2 font-medium text-sm whitespace-nowrap transition-colors ${
                activeTab === tab?.id
                  ? 'border-primary text-primary' :'border-transparent text-muted-foreground hover:text-foreground'
              }`}
            >
              {tab?.label}
            </button>
          ))}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'dashboard' && <ScoringDashboard stats={scoringStats} leads={priorityLeads} />}
      {activeTab === 'prioritization' && <PrioritizationQueue leads={priorityLeads} onContact={handleLeadContacted} />}
      {activeTab === 'configuration' && <ScoringConfiguration factors={scoringFactors} />}
      {activeTab === 'breakdown' && <ScoringBreakdown leads={priorityLeads} onScoreUpdate={handleScoreUpdate} />}
    </PageLayout>
  );
}